import type { FlatJoinSplitV2LogProvider } from "./merkleIndex";

export interface HistoryCheckpoint<T> {
  block: number;
  hash: string;
  value: T;
}

/** Public history only. A checkpoint saves RPC work, never a finality decision.
 * Recheck both ends around each read; failures cannot advance the checkpoint.
 * Optional persistence must authenticate its value against the pinned chain
 * before returning it; disk metadata is never a finality decision. */
export class PinnedHistory<T> {
  private readonly providers = new WeakMap<
    FlatJoinSplitV2LogProvider,
    {
      checkpoints: Map<string, HistoryCheckpoint<T>>;
      pending: Map<string, Promise<unknown>>;
    }
  >();

  constructor(private readonly retain: (value: T) => boolean) {}

  async read(params: {
    provider: FlatJoinSplitV2LogProvider;
    key: string;
    block: number;
    hash?: string;
    build: (
      previous: HistoryCheckpoint<T> | undefined,
      pinnedHash: string,
    ) => Promise<T>;
    validate: (value: T) => void;
    load?: () => Promise<HistoryCheckpoint<T> | undefined>;
    save?: (checkpoint: HistoryCheckpoint<T>) => Promise<void>;
  }): Promise<T> {
    if (!Number.isSafeInteger(params.block) || params.block < 0)
      throw new Error("Public history block is invalid");
    let state = this.providers.get(params.provider);
    if (!state) {
      state = { checkpoints: new Map(), pending: new Map() };
      this.providers.set(params.provider, state);
    }
    const { checkpoints, pending } = state;
    const pin = async (block: number, expected?: string) => {
      const result = await params.provider.getBlock(block);
      if (
        !result ||
        result.number !== block ||
        !/^0x[0-9a-fA-F]{64}$/.test(result.hash || "") ||
        (expected && result.hash!.toLowerCase() !== expected.toLowerCase())
      )
        throw new Error(
          "Public history canonical block changed or is unavailable",
        );
      return result.hash!.toLowerCase();
    };
    // Concurrent callers share the completed prefix, while each caller still
    // checks its own block, root and count constraints after the prior read.
    const work = (pending.get(params.key) || Promise.resolve())
      .catch(() => undefined)
      .then(async () => {
        let previous = checkpoints.get(params.key) ?? (await params.load?.());
        if (previous && previous.block > params.block) previous = undefined;
        const [hash, priorBlock] = await Promise.all([
          pin(params.block, params.hash),
          previous && previous.block !== params.block
            ? params.provider.getBlock(previous.block)
            : Promise.resolve(undefined),
        ]);
        if (previous) {
          const canonical =
            previous.block === params.block
              ? { number: params.block, hash }
              : priorBlock;
          if (
            canonical?.number !== previous.block ||
            canonical.hash?.toLowerCase() !== previous.hash
          ) {
            checkpoints.delete(params.key);
            previous = undefined;
          }
        }
        const value =
          previous?.block === params.block
            ? previous.value
            : await params.build(previous, hash);
        try {
          params.validate(value);
          await Promise.all([
            pin(params.block, hash),
            previous && previous.block !== params.block
              ? pin(previous.block, previous.hash)
              : Promise.resolve(),
          ]);
        } catch (error) {
          checkpoints.delete(params.key);
          throw error;
        }
        if (this.retain(value)) {
          // A reader of an older safe block must not evict a newer prefix.
          if ((checkpoints.get(params.key)?.block ?? -1) <= params.block) {
            checkpoints.delete(params.key);
            checkpoints.set(params.key, { block: params.block, hash, value });
            // Cache failure must not discard a successfully verified balance.
            void params
              .save?.({ block: params.block, hash, value })
              .catch(() => undefined);
            while (checkpoints.size > 8)
              checkpoints.delete(checkpoints.keys().next().value!);
          }
        } else checkpoints.delete(params.key);
        return value;
      });
    pending.set(params.key, work);
    try {
      return await work;
    } finally {
      if (pending.get(params.key) === work) pending.delete(params.key);
    }
  }
}
